import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, Package, ArrowLeft, Plus, Minus, Star } from 'lucide-react';
import Layout from '../Layout';
import { API_BASE_URL, getData } from '../../helpers/ApiSettings';
import { SaleCartManager } from '../../helpers/SaleCartManager';
import { showToast } from '../../helpers/customToast';
import { checkIsReferenced } from '../../helpers/user';
import { useDeliveryThreshold } from '../../helpers/useDeliveryThreshold';

const ProductSaleDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isReferenced, setIsReferenced] = useState(false);
  const freeDeliveryThreshold = useDeliveryThreshold();
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    fetchProduct();
    checkIsReferenced().then(setIsReferenced);
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await getData(`/product-sales?filters[documentId][$eq]=${id}&populate[images]=*&populate[sale_category]=*`);
      const productData = response.data.data[0];

      if (!productData) {
        throw new Error('Product not found');
      }

      setProduct(productData);

      // Fetch related products from same category
      if (productData.sale_category?.id) {
        fetchRelatedProducts(productData.sale_category.id, productData.id);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      showToast('Produit introuvable', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedProducts = async (categoryId, excludeId) => {
    try {
      const response = await getData(
        `/product-sales?filters[sale_category][id][$eq]=${categoryId}&filters[id][$ne]=${excludeId}&filters[is_active][$eq]=true&populate[images]=*&populate[sale_category]=*&pagination[limit]=4`
      );
      setRelatedProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching related products:', error);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    const success = SaleCartManager.addToCart(product, quantity);
    if (success) {
      showToast(
        `${product.title} ajouté au panier`, 
        'success'
      );
    } else {
      showToast('Erreur lors de l\'ajout au panier', 'error');
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  const getCurrentPrice = () => {
    if (!product) return 0;

    // Bulk pricing wins when quantity threshold is met
    if (product.bulk_price && product.bulk_quantity && quantity >= product.bulk_quantity) {
      return product.bulk_price;
    }

    // Tarif vente privée : appliqué UNIQUEMENT si le visiteur est référencé.
    const hasPrivateOffer =
      (product.is_private === true || product.sale_category?.is_private === true) &&
      product.private_price != null;
    if (hasPrivateOffer && isReferenced) {
      return product.private_price;
    }

    return product.unit_price;
  };

  const getTotalPrice = () => {
    return getCurrentPrice() * quantity;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-danger">
            Produit introuvable
          </div>
          <Link to="/vente" className="btn btn-primary">
            <ArrowLeft size={16} className="me-2" />
            Retour à la boutique
          </Link>
        </div>
      </Layout>
    );
  }

  const hasBulkOption = product.bulk_price && product.bulk_quantity > 1;

  return (
    <Layout>
      <div className="container my-5">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/vente">Boutique</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {product.title}
            </li>
          </ol>
        </nav>

        <div className="row">
          {/* Product Images */}
          <div className="col-md-6">
            <div className="mb-3">
              <img
                src={API_BASE_URL + product.images[selectedImage]?.url}
                alt={product.title}
                className="img-fluid rounded"
                style={{ width: '100%', height: '400px', objectFit: 'cover' }}
              />
            </div>
            
            {product.images.length > 1 && (
              <div className="row">
                {product.images.map((image, index) => (
                  <div key={index} className="col-3">
                    <img
                      src={API_BASE_URL + image.url}
                      alt={`${product.title} ${index + 1}`}
                      className={`img-fluid rounded cursor-pointer ${selectedImage === index ? 'border border-primary' : ''}`}
                      style={{ height: '80px', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setSelectedImage(index)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="col-md-6">
            <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
              <h1 className="mb-0">{product.title}</h1>
              {product.featured && (
                <span className="badge bg-warning text-dark">
                  <Star size={14} className="me-1" />
                  Vedette
                </span>
              )}
              {(product.is_private === true || product.sale_category?.is_private === true) &&
                product.private_price != null &&
                isReferenced && (
                <span className="badge bg-dark">Tarif vente privée</span>
              )}
            </div>

            {product.brand && (
              <p className="text-muted mb-3">Marque: {product.brand}</p>
            )}

            <div className="mb-4">
              <div dangerouslySetInnerHTML={{ __html: product.description }} />
            </div>

            {/* Product Details */}
            <div className="mb-4">
              {product.age_range && (
                <p><strong>Âge recommandé:</strong> {product.age_range}</p>
              )}
              {product.weight && (
                <p><strong>Poids:</strong> {product.weight}g</p>
              )}
              {/* <p>
                <Package size={16} className="me-2" />
                <strong>Stock disponible:</strong> {product.stock_quantity}
              </p> */}
            </div>

            {/* Pricing Options */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">Tarification</h5>
                
                {(() => {
                  const hasPrivateOffer =
                    (product.is_private === true || product.sale_category?.is_private === true) &&
                    product.private_price != null;
                  const showsPrivatePrice = hasPrivateOffer && isReferenced;
                  return (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Prix unitaire (1-{hasBulkOption ? product.bulk_quantity - 1 : '∞'}):</span>
                      {showsPrivatePrice ? (
                        <strong>
                          <span className="text-decoration-line-through text-muted me-2 small">
                            {product.unit_price.toFixed(2)}€
                          </span>
                          <span className="text-success">{product.private_price.toFixed(2)}€</span>
                        </strong>
                      ) : (
                        <strong>{product.unit_price.toFixed(2)}€</strong>
                      )}
                    </div>
                  );
                })()}

                {hasBulkOption && (
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>Prix unitaire (à partir de {product.bulk_quantity}):</span>
                    <strong className="text-success">{product.bulk_price}€</strong>
                  </div>
                )}

                {hasBulkOption && (
                  <div className="alert alert-info mt-3">
                    <small>
                      <strong>Prix dégressif:</strong> À partir de {product.bulk_quantity} articles, 
                      le prix unitaire passe à {product.bulk_price}€ 
                      (économie de {(product.unit_price - product.bulk_price).toFixed(2)}€ par article)
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="mb-4">
              <label className="form-label">Quantité</label>
              <div className="input-group" style={{width: '150px'}}>
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleQuantityChange(-1)}
                  disabled={quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  className="form-control text-center"
                  value={quantity}
                  onChange={(e) => {
                    const newQty = Math.max(1, parseInt(e.target.value) || 1);
                    setQuantity(newQty);
                  }}
                  min="1"
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleQuantityChange(1)}
                >
                  <Plus size={16} />
                </button>
              </div>
              <small className="text-muted">
                Prix unitaire: {getCurrentPrice()}€ - Total: {getTotalPrice().toFixed(2)}€
                {hasBulkOption && quantity >= product.bulk_quantity && (
                  <span className="text-success d-block">✓ Prix dégressif appliqué!</span>
                )}
              </small>
            </div>

            {/* Add to Cart */}
            <div className="mb-4">
              <button
                className="btn btn-primary btn-lg w-100"
                onClick={handleAddToCart}
              >
                <ShoppingCart size={20} className="me-2" />
                Ajouter au panier - {getTotalPrice().toFixed(2)}€
              </button>
            </div>

            {/* Free delivery info */}
            <div className="alert alert-info">
              <strong>Livraison gratuite</strong> à partir de {freeDeliveryThreshold}€ d'achat !
            </div>
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-5">
            <h3>Produits similaires</h3>
            <div className="row">
              {relatedProducts.map(relatedProduct => (
                <div key={relatedProduct.id} className="col-lg-3 col-md-4 col-sm-6 mb-4">
                  <div className="card h-100">
                    <Link to={`/vente/produit/${relatedProduct.documentId}`}>
                      <img
                        src={API_BASE_URL + relatedProduct.images[0]?.url}
                        alt={relatedProduct.title}
                        className="card-img-top"
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    </Link>
                    <div className="card-body">
                      <h6 className="card-title">
                        <Link 
                          to={`/vente/produit/${relatedProduct.documentId}`}
                          className="text-decoration-none"
                        >
                          {relatedProduct.title}
                        </Link>
                      </h6>
                      <p className="text-muted mb-0">
                        {((relatedProduct.is_private === true || relatedProduct.sale_category?.is_private === true)
                          && relatedProduct.private_price != null
                          && isReferenced
                          ? relatedProduct.private_price
                          : relatedProduct.unit_price
                        ).toFixed(2)}€
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProductSaleDetailPage;